import React from "react";
import { Link, Navigate, Route, Routes } from "react-router-dom";
import { useUserContext } from "../../context/UserProvider";
import { ArrowBackIcon } from "../util/Icons";
import SubMenuWrapper, { SubMenuItem } from "../util/SubMenu";
import CalorieDeficitCalculator from "./components/CalorieDeficitCalculator";
import MonthlyMeetups from "./components/MonthlyMeetups";
import ProgramPage from "./components/ProgramPage";

// Program data configuration
const PROGRAMS = [
    {
        id: "calorie-deficit-calculator",
        title: "Calorie Deficit Calculator & Mini-Course",
        description: "Estimate maintenance calories and pick a sensible daily deficit target.",
        icon: "🔥",
        path: "./calorie-deficit-calculator",
        comingSoon: false,
        memberOnly: false,
    },
    {
        id: "strength-training",
        title: "4-Week Strength Training for Runners",
        description: "Build power and endurance with a runner-focused strength program.",
        icon: "💪",
        path: "./strength-training",
        comingSoon: false,
        memberOnly: true,
    },
    {
        id: "strength-for-life",
        title: "Strength for Life",
        description: "A 12-week beginner strength plan with bodyweight, dumbbell, and resistance band options.",
        icon: "🏡",
        path: "./strength-for-life",
        comingSoon: false,
        memberOnly: true,
    },
    {
        id: "mobility",
        title: "21-Days Mobility Program",
        description: "Improve flexibility and reduce injury risk with daily mobility work.",
        icon: "🧘",
        path: "./mobility",
        comingSoon: false,
        memberOnly: true,
    },
    {
        id: "oneHundred",
        title: "101 Body Weight Exercises",
        description: "A comprehensive library of bodyweight exercises you can do anywhere.",
        icon: "🏋️",
        path: "./oneHundred",
        comingSoon: false,
        memberOnly: true,
    },
    {
        id: "monthly-meetups",
        title: "Monthly Meetups",
        description: "Watch member meetup recordings and revisit the show notes from each session.",
        icon: "🎥",
        path: "./monthly-meetups",
        comingSoon: false,
        memberOnly: true,
    },
];

export default function StrongHealthy() {
    const { canAccessHealthHub } = useUserContext();

    if (!canAccessHealthHub) {
        return (
            <div className="min-h-screen py-8 px-2 md:px-4" style={{ background: '#ffffff' }}>
                <div className="max-w-4xl mx-auto">
                    <SubMenuWrapper>
                        <SubMenuItem icon={<ArrowBackIcon size={24} color="#6b7280" />} label={"Home"} to={"/"} />
                    </SubMenuWrapper>

                    <div className="text-center mt-8">
                        <div className="rounded-3xl p-8" style={{
                            background: 'linear-gradient(135deg, #f1f5f9 0%, #e0e7ff 100%)',
                            border: '1px solid rgba(99, 102, 241, 0.15)'
                        }}>
                            <span className="text-6xl mb-4 block">🔒</span>
                            <h2 className="text-2xl font-bold mb-4" style={{ color: '#1e293b' }}>
                                Health Hub Locked
                            </h2>
                            <p className="text-base mb-6" style={{ color: '#64748b' }}>
                                The Health Hub is available to members and athletes with a valid challenge registration.
                            </p>
                            <Link
                                to="/membership"
                                className="inline-block rounded-2xl px-8 py-4 font-bold text-lg transition-all duration-300 hover:scale-[1.03] hover:shadow-2xl no-underline"
                                style={{
                                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                                    color: 'white',
                                    boxShadow: '0 10px 40px rgba(99, 102, 241, 0.3)'
                                }}
                            >
                                Become a Member
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <Routes>
            <Route path="/" element={
                <div className="min-h-screen py-8 px-2 md:px-4" style={{ background: '#ffffff' }}>
                    <div className="max-w-4xl mx-auto">
                        <SubMenuWrapper>
                            <SubMenuItem icon={<ArrowBackIcon size={24} color="#6b7280" />} label={"Home"} to={"/"} />
                        </SubMenuWrapper>

                        <div className="text-center mb-6 mt-4">
                            <span className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-3" style={{
                                background: 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)',
                                color: 'white'
                            }}>
                                💪 Health Hub
                            </span>
                            <h1 className="text-3xl md:text-4xl font-extrabold mb-3" style={{
                                background: 'linear-gradient(90deg, #1e293b 0%, #334155 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent'
                            }}>
                                Strong & Healthy Hub
                            </h1>
                            <p className="text-base md:text-lg" style={{ color: '#64748b' }}>
                                Tools and programs designed to make you a stronger, healthier and more resilient athlete.
                            </p>
                        </div>

                        <div className="grid gap-4 md:grid-cols-2 md:gap-4">
                            {PROGRAMS.map((program) => (
                                <ProgramCard key={program.id} program={program} />
                            ))}
                        </div>
                    </div>
                </div>

            } />
            <Route path="/calorie-deficit-calculator" element={<CalorieDeficitCalculator />} />
            <Route path="/monthly-meetups/*" element={<MonthlyMeetups />} />
            <Route path="/:programId/*" element={<ProgramPage />} />
            <Route path="/*" element={<Navigate to={"./"} />} />
        </Routes>
    );
}

function ProgramCard({ program }) {
    const cardContent = (
        <div
            className={`rounded-2xl p-5 md:p-6 transition-all duration-300 ${program.comingSoon ? 'opacity-60' : 'hover:scale-[1.02] hover:shadow-xl cursor-pointer'
                }`}
            style={{
                background: program.comingSoon
                    ? 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)'
                    : 'linear-gradient(135deg, #f1f5f9 0%, #e0e7ff 100%)',
                border: '1px solid rgba(99, 102, 241, 0.15)'
            }}
        >
            <div className="flex items-start gap-4">
                <span className="text-4xl">{program.icon}</span>
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-bold m-0" style={{ color: '#1e293b' }}>
                            {program.title}
                        </h3>
                        {program.memberOnly && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{
                                background: 'rgba(15, 23, 42, 0.08)',
                                color: '#334155'
                            }}>
                                Members Only
                            </span>
                        )}
                        {program.comingSoon && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{
                                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                color: 'white'
                            }}>
                                Coming Soon
                            </span>
                        )}
                    </div>
                    <p className="text-sm m-0" style={{ color: '#64748b' }}>
                        {program.description}
                    </p>
                </div>
            </div>
        </div>
    );

    if (program.comingSoon) {
        return cardContent;
    }

    return (
        <Link to={program.path} className="no-underline">
            {cardContent}
        </Link>
    );
}
